/**
 * CameraManager - Manages WebRTC camera broadcasting, local preview, and peer media streams
 * for Human vs Human online multiplayer games.
 */
import { appStorage } from '../storage/IndexedDBStorage.js';

function getStorage() {
  return (typeof window !== 'undefined' && window.appStorage) || appStorage || (typeof localStorage !== 'undefined' ? localStorage : null);
}

export class CameraManager {
  constructor() {
    this.localStream = null;
    this.isBroadcasting = false;
    this.isPaused = false;
    this.isLocalMinimized = false;
    this.activeCalls = new Map(); // peerId -> MediaConnection
    this.remoteStreams = new Map(); // seatId -> { stream, peerId, name, paused }
    this.hiddenSeats = new Set(); // seatIds hidden/minimized locally by player
    this.minimizedSeats = this.hiddenSeats;
  }

  /**
   * Returns singleton instance on window if available.
   */
  static getInstance() {
    if (typeof window !== 'undefined') {
      if (!window._ccCameraManager) {
        window._ccCameraManager = new CameraManager();
      }
      return window._ccCameraManager;
    }
    if (!CameraManager._instance) {
      CameraManager._instance = new CameraManager();
    }
    return CameraManager._instance;
  }

  /**
   * Checks if camera media devices are supported in current environment.
   * @returns {boolean}
   */
  static isSupported() {
    return typeof navigator !== 'undefined' &&
      !!navigator.mediaDevices &&
      typeof navigator.mediaDevices.getUserMedia === 'function';
  }

  /**
   * Starts broadcasting local webcam to all connected online peers.
   * @param {Object} [context]
   * @returns {Promise<MediaStream|null>}
   */
  async startBroadcast(context = {}) {
    if (!CameraManager.isSupported()) {
      const setStatus = context.setStatus || (typeof window !== 'undefined' ? window.setStatus : () => {});
      setStatus('Camera access is not supported by this browser.');
      return null;
    }

    if (this.isBroadcasting && this.localStream) {
      if (this.isPaused) {
        return this.resumeBroadcast(context);
      }
      return this.localStream;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 480, max: 640 },
          height: { ideal: 360, max: 480 },
          facingMode: 'user'
        },
        audio: false
      });

      this.localStream = stream;
      this.isBroadcasting = true;
      this.isPaused = false;

      // Listen for unexpected track ending (e.g. user unplugs camera or revokes permission)
      stream.getVideoTracks().forEach(track => {
        track.onended = () => {
          this.stopBroadcast(context);
        };
      });

      // Render local preview on table
      this.renderLocalPreview(context);

      // Call all connected peers
      this.callAllConnectedPeers(context);

      // Notify peers of our active camera state
      this.broadcastCamState(true, false, context);

      this.updateCamButtonUi(context);
      return stream;
    } catch (err) {
      console.warn('CameraManager.startBroadcast error:', err);
      const setStatus = context.setStatus || (typeof window !== 'undefined' ? window.setStatus : () => {});
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setStatus('Camera permission was denied. Please allow camera access in your browser.');
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        setStatus('No camera device was detected on your system.');
      } else {
        setStatus('Unable to start camera: ' + (err.message || err.name || 'unknown error'));
      }
      return null;
    }
  }

  /**
   * Pauses or unpauses the local video broadcast without dropping the peer connection.
   * @param {Object} [context]
   * @returns {boolean} New paused state
   */
  pauseBroadcast(context = {}) {
    if (!this.localStream || !this.isBroadcasting) return false;

    this.isPaused = !this.isPaused;
    const tracks = this.localStream.getVideoTracks();
    tracks.forEach(track => {
      track.enabled = !this.isPaused;
    });

    this.broadcastCamState(true, this.isPaused, context);
    this.renderLocalPreview(context);
    this.updateCamButtonUi(context);
    return this.isPaused;
  }

  /**
   * Resumes broadcasting if currently paused.
   * @param {Object} [context]
   * @returns {MediaStream|null}
   */
  resumeBroadcast(context = {}) {
    if (this.isPaused) {
      this.pauseBroadcast(context);
    }
    return this.localStream;
  }

  /**
   * Completely stops local camera broadcast and closes all media calls.
   * @param {Object} [context]
   */
  stopBroadcast(context = {}) {
    if (this.localStream) {
      try {
        this.localStream.getTracks().forEach(track => {
          try { track.stop(); } catch (e) {}
        });
      } catch (e) {}
      this.localStream = null;
    }

    this.isBroadcasting = false;
    this.isPaused = false;

    // Close any outgoing media calls
    for (const [peerId, call] of this.activeCalls.entries()) {
      try { call.close(); } catch (e) {}
    }
    this.activeCalls.clear();

    // Notify peers that camera is off
    this.broadcastCamState(false, false, context);

    // Remove local preview
    const doc = context.document || (typeof document !== 'undefined' ? document : null);
    if (doc) {
      const localBox = doc.getElementById('localCamPreviewWrap');
      if (localBox) localBox.remove();
    }

    this.updateCamButtonUi(context);
  }

  /**
   * Makes outgoing PeerJS media calls to all known connected remote peers.
   * @param {Object} [context]
   */
  callAllConnectedPeers(context = {}) {
    if (!this.localStream || !this.isBroadcasting) return;

    const net = context.net || (typeof window !== 'undefined' ? window.net : null);
    if (!net || !net.peer) return;

    // If host: call all clients in net.clients
    if (net.isHost && net.clients) {
      for (const [peerId, client] of net.clients.entries()) {
        this.callPeer(peerId, client.seatId, context);
      }
    } else if (!net.isHost && net.room) {
      // If guest: call the host ('ccb-' + room)
      const hostPeerId = 'ccb-' + net.room.trim().toUpperCase();
      this.callPeer(hostPeerId, 'p1', context);
    }
  }

  /**
   * Calls an individual remote peer with our local camera stream.
   * @param {string} remotePeerId
   * @param {string} [remoteSeatId]
   * @param {Object} [context]
   */
  callPeer(remotePeerId, remoteSeatId = null, context = {}) {
    if (!remotePeerId || !this.localStream || !this.isBroadcasting) return;
    const net = context.net || (typeof window !== 'undefined' ? window.net : null);
    if (!net || !net.peer || net.peer.destroyed) return;

    // If existing call was closed, clean it up
    const existingCall = this.activeCalls.get(remotePeerId);
    if (existingCall) {
      if (existingCall.open) return;
      try { existingCall.close(); } catch (e) {}
      this.activeCalls.delete(remotePeerId);
    }

    const doc = context.document || (typeof document !== 'undefined' ? document : null);
    const s = getStorage();
    const myName = (doc && doc.getElementById('optPlayerName') && doc.getElementById('optPlayerName').value.trim())
      || (s ? (s.getItem('cc_player_name') || '').trim() : '')
      || (net.isHost ? 'Host' : 'Guest');
    const mySeatId = (net && net.mySeatId) || (net.isHost ? 'p1' : 'p2');

    try {
      const call = net.peer.call(remotePeerId, this.localStream, {
        metadata: {
          seatId: mySeatId,
          name: myName
        }
      });
      if (!call) return;

      this.activeCalls.set(remotePeerId, call);
      this.bindCallEvents(call, remoteSeatId, context, myName);
    } catch (e) {
      console.warn('[CameraManager] callPeer error to', remotePeerId, e);
    }
  }

  /**
   * Handles an incoming PeerJS media call from a remote peer.
   * @param {Object} call
   * @param {Object} [context]
   */
  handleIncomingCall(call, context = {}) {
    if (!call) return;
    const remotePeerId = call.peer;
    this.activeCalls.set(remotePeerId, call);

    const meta = call.metadata || {};
    let remoteSeatId = meta.seatId || null;
    let remoteName = meta.name || null;

    // Answer with our local stream if active; otherwise answer with empty options
    try {
      if (this.localStream && this.isBroadcasting) {
        call.answer(this.localStream);
      } else {
        call.answer();
      }
    } catch (e) {
      console.warn('[CameraManager] call.answer error:', e);
    }

    const net = context.net || (typeof window !== 'undefined' ? window.net : null);
    if (!remoteSeatId && net) {
      if (net.isHost && net.clients && net.clients.get(remotePeerId)) {
        remoteSeatId = net.clients.get(remotePeerId).seatId;
        if (!remoteName) remoteName = net.clients.get(remotePeerId).name;
      } else if (!net.isHost) {
        remoteSeatId = 'p1';
      }
    }

    this.bindCallEvents(call, remoteSeatId, context, remoteName);
  }

  /**
   * Binds stream, close, and error events to a PeerJS MediaConnection.
   * @param {Object} call
   * @param {string} [targetSeatId]
   * @param {Object} [context]
   * @param {string} [initialName]
   */
  bindCallEvents(call, targetSeatId = null, context = {}, initialName = null) {
    const remotePeerId = call.peer;

    call.on('stream', (remoteStream) => {
      console.log('[CameraManager] Remote stream arrived from', remotePeerId, remoteStream);
      const meta = call.metadata || {};
      const resolvedSeatId = targetSeatId || meta.seatId || this.resolveSeatIdForPeer(remotePeerId, context) || 'p2';
      const resolvedName = initialName || meta.name || this.resolveNameForSeat(resolvedSeatId, context);

      this.remoteStreams.set(resolvedSeatId, {
        stream: remoteStream,
        peerId: remotePeerId,
        name: resolvedName,
        paused: false
      });

      this.renderRemoteTile(resolvedSeatId, remoteStream, resolvedName, false, context);
    });

    call.on('close', () => {
      this.activeCalls.delete(remotePeerId);
      const sId = targetSeatId || this.resolveSeatIdForPeer(remotePeerId, context);
      if (sId) {
        this.remoteStreams.delete(sId);
        this.removeRemoteTile(sId, context);
      }
    });

    call.on('error', (err) => {
      console.warn('Call error from', remotePeerId, err);
      this.activeCalls.delete(remotePeerId);
    });
  }

  /**
   * Handles peer network data message for camera state updates ({ type: 'cam_state' }).
   * @param {Object} msg
   * @param {Object} [context]
   */
  handleCamStateMessage(msg, context = {}) {
    if (!msg || msg.type !== 'cam_state') return;

    const seatId = msg.seatId || this.resolveSeatIdForPeer(msg.peerId, context) || 'p2';
    const active = msg.active !== false;
    const paused = Boolean(msg.paused);

    const existing = this.remoteStreams.get(seatId);
    if (existing) {
      existing.paused = paused;
    }

    if (!active) {
      this.remoteStreams.delete(seatId);
      this.removeRemoteTile(seatId, context);
    } else {
      const name = msg.name || this.resolveNameForSeat(seatId, context);
      const stream = existing ? existing.stream : null;
      if (stream) {
        this.renderRemoteTile(seatId, stream, name, paused, context);
      }
    }
  }

  /**
   * Broadcasts our current camera state (active/paused) to all connected peers.
   * @param {boolean} active
   * @param {boolean} paused
   * @param {Object} [context]
   */
  broadcastCamState(active, paused, context = {}) {
    const net = context.net || (typeof window !== 'undefined' ? window.net : null);
    if (!net) return;

    const mySeat = context.mySeat || (typeof window !== 'undefined' && window.mySeat ? window.mySeat : () => 'p1');
    const mySeatId = (net && net.mySeatId) || mySeat();
    const doc = context.document || (typeof document !== 'undefined' ? document : null);
    const s = getStorage();
    const myName = (doc && doc.getElementById('optPlayerName') && doc.getElementById('optPlayerName').value.trim())
      || (s ? (s.getItem('cc_player_name') || '').trim() : '')
      || 'Player';

    const payload = {
      type: 'cam_state',
      active,
      paused,
      seatId: mySeatId,
      peerId: net.peer ? net.peer.id : '',
      name: myName
    };

    if (net.isHost && net.clients) {
      for (const client of net.clients.values()) {
        if (client.conn && client.conn.open !== false) {
          try { client.conn.send(payload); } catch (e) {}
        }
      }
    } else if (!net.isHost && net.conn && net.conn.open !== false) {
      try { net.conn.send(payload); } catch (e) {}
    }
  }

  /**
   * Toggles hiding/showing a specific opponent's camera on our local screen.
   * @param {string} seatId
   * @param {Object} [context]
   * @returns {boolean} New minimized/hidden state
   */
  toggleHideRemoteCam(seatId, context = {}) {
    if (this.hiddenSeats.has(seatId)) {
      this.hiddenSeats.delete(seatId);
    } else {
      this.hiddenSeats.add(seatId);
    }
    const isMinimized = this.hiddenSeats.has(seatId);

    const doc = context.document || (typeof document !== 'undefined' ? document : null);
    if (doc) {
      const tile = doc.getElementById(`remoteCamTile_${seatId}`);
      if (tile) {
        tile.classList.toggle('is-hidden', isMinimized);
        tile.classList.toggle('is-minimized', isMinimized);
        const toggleBtn = tile.querySelector('.btn-cam-hide-toggle');
        const videoEl = tile.querySelector('video');
        if (toggleBtn) {
          toggleBtn.textContent = isMinimized ? '▲ Expand' : '▼ Minimize';
          toggleBtn.title = isMinimized ? 'Expand camera' : 'Minimize camera';
        }
        if (videoEl) {
          videoEl.style.display = isMinimized ? 'none' : 'block';
        }
        const videoWrap = tile.querySelector('.cam-video-wrap');
        if (videoWrap) {
          videoWrap.style.display = isMinimized ? 'none' : 'block';
        }
        const resizeHandle = tile.querySelector('.cam-resize-handle');
        if (resizeHandle) {
          resizeHandle.style.display = isMinimized ? 'none' : 'flex';
        }
      }
    }
    return isMinimized;
  }

  /**
   * Toggles minimizing or expanding an individual remote opponent's camera tile.
   * @param {string} seatId
   * @param {Object} [context]
   * @returns {boolean} New minimized state
   */
  toggleMinimizeRemoteCam(seatId, context = {}) {
    return this.toggleHideRemoteCam(seatId, context);
  }

  /**
   * Toggles minimizing or expanding any camera on screen (local preview or remote opponent).
   * @param {string} [seatId] 'local' (default) or opponent seatId ('p1', 'p2', etc.)
   * @param {Object} [context]
   * @returns {boolean} New minimized state
   */
  toggleMinimizeCam(seatId = 'local', context = {}) {
    if (!seatId || seatId === 'local') {
      return this.toggleMinimizeLocalCam(context);
    }
    return this.toggleMinimizeRemoteCam(seatId, context);
  }

  /**
   * Minimizes or expands all active cameras (local and remote).
   * @param {boolean} [minimize] Force state; if omitted toggles based on whether all are minimized.
   * @param {Object} [context]
   * @returns {boolean} Whether all cameras are now minimized
   */
  toggleMinimizeAll(minimize = undefined, context = {}) {
    const shouldMinimize = minimize !== undefined ? minimize : !this.areAllMinimized();

    if (this.localStream && this.isLocalMinimized !== shouldMinimize) {
      this.toggleMinimizeLocalCam(context);
    }

    for (const seatId of this.remoteStreams.keys()) {
      const isMin = this.hiddenSeats.has(seatId);
      if (isMin !== shouldMinimize) {
        this.toggleHideRemoteCam(seatId, context);
      }
    }

    return shouldMinimize;
  }

  /**
   * Checks whether all active cameras (local and remote) are currently minimized.
   * @returns {boolean}
   */
  areAllMinimized() {
    if (this.localStream && !this.isLocalMinimized) return false;
    for (const seatId of this.remoteStreams.keys()) {
      if (!this.hiddenSeats.has(seatId)) return false;
    }
    return true;
  }

  /**
   * Toggles minimizing or expanding the local player's camera preview window.
   * When minimized, hides the video feed and resize handle while keeping
   * the pause and turn off controls visible.
   * @param {Object} [context]
   * @returns {boolean} New minimized state
   */
  toggleMinimizeLocalCam(context = {}) {
    this.isLocalMinimized = !this.isLocalMinimized;
    const doc = context.document || (typeof document !== 'undefined' ? document : null);
    if (doc) {
      const localWrap = doc.getElementById('localCamPreviewWrap');
      if (localWrap) {
        localWrap.classList.toggle('is-minimized', this.isLocalMinimized);
        const toggleBtn = localWrap.querySelector('.btn-cam-minimize-local');
        if (toggleBtn) {
          toggleBtn.textContent = this.isLocalMinimized ? '▲ Expand' : '▼ Minimize';
          toggleBtn.title = this.isLocalMinimized ? 'Expand camera preview' : 'Minimize camera preview';
        }
        const videoWrap = localWrap.querySelector('.cam-video-wrap');
        if (videoWrap) {
          videoWrap.style.display = this.isLocalMinimized ? 'none' : 'block';
        }
        const resizeHandle = localWrap.querySelector('.cam-resize-handle');
        if (resizeHandle) {
          resizeHandle.style.display = this.isLocalMinimized ? 'none' : 'flex';
        }
      }
    }
    return this.isLocalMinimized;
  }

  /**
   * Safely attaches and autoplays a MediaStream on a video element with full
   * browser autoplay policy compliance (muted, playsinline, loadedmetadata hooks).
   * @param {HTMLVideoElement} videoEl
   * @param {MediaStream} stream
   * @param {Object} [doc]
   */
  _attachStreamToVideo(videoEl, stream, doc = null) {
    if (!videoEl || !stream) return;

    videoEl.muted = true;
    videoEl.defaultMuted = true;
    videoEl.playsInline = true;
    if (typeof videoEl.setAttribute === 'function') {
      videoEl.setAttribute('muted', '');
      videoEl.setAttribute('playsinline', '');
      videoEl.setAttribute('autoplay', '');
    }
    if (videoEl.classList && typeof videoEl.classList.add === 'function') {
      videoEl.classList.add('cam-video');
    }

    if (videoEl.srcObject !== stream) {
      videoEl.srcObject = stream;
    }

    const removePlayOverlay = () => {
      if (videoEl.parentElement) {
        const overlay = videoEl.parentElement.querySelector('.cam-click-to-play');
        if (overlay) overlay.remove();
      }
    };

    const showPlayOverlay = () => {
      if (!doc || !videoEl.parentElement) return;
      if (videoEl.parentElement.querySelector('.cam-click-to-play')) return;
      const btn = doc.createElement('button');
      btn.type = 'button';
      btn.className = 'cam-click-to-play';
      btn.innerHTML = '▶ Click to Play';
      btn.title = 'Click to start video stream';
      btn.onclick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        videoEl.muted = true;
        videoEl.play().then(removePlayOverlay).catch(err => console.warn('User play failed:', err));
      };
      videoEl.parentElement.appendChild(btn);
    };

    const attemptPlay = () => {
      try {
        const p = videoEl.play();
        if (p && typeof p.then === 'function') {
          p.then(removePlayOverlay).catch(err => {
            console.warn('[CameraManager] Autoplay prevented, showing play button:', err);
            showPlayOverlay();
          });
        }
      } catch (e) {
        console.warn('[CameraManager] Play call error:', e);
        showPlayOverlay();
      }
    };

    videoEl.onloadedmetadata = () => attemptPlay();
    videoEl.oncanplay = () => attemptPlay();
    attemptPlay();

    if (stream && typeof stream.getVideoTracks === 'function') {
      stream.getVideoTracks().forEach(track => {
        track.onunmute = () => {
          attemptPlay();
        };
      });
    }
  }

  /**
   * Renders the local player's selfie PiP camera preview card in #youZone.
   * @param {Object} [context]
   */
  renderLocalPreview(context = {}) {
    const doc = context.document || (typeof document !== 'undefined' ? document : null);
    if (!doc || !this.localStream) return;

    let localWrap = doc.getElementById('localCamPreviewWrap');
    const s = getStorage();
    const myName = (doc.getElementById('optPlayerName') && doc.getElementById('optPlayerName').value.trim())
      || (s ? (s.getItem('cc_player_name') || '').trim() : '')
      || 'You';

    if (!localWrap) {
      const targetParent = doc.getElementById('localCamContainer') || doc.getElementById('youZone');
      if (!targetParent) return;

      localWrap = doc.createElement('div');
      localWrap.id = 'localCamPreviewWrap';
      localWrap.className = `cam-tile cam-tile-local ${this.isLocalMinimized ? 'is-minimized' : ''}`;
      localWrap.innerHTML = `
        <div class="cam-header">
          <span class="cam-name">📹 ${myName} (You)</span>
          <div class="cam-header-right">
            <span class="cam-badge ${this.isPaused ? 'cam-badge-paused' : 'cam-badge-live'}">
              ${this.isPaused ? '⏸️ Paused' : '● Live'}
            </span>
            <button type="button" class="btn-cam-hide-toggle btn-cam-minimize-local" title="${this.isLocalMinimized ? 'Expand camera preview' : 'Minimize camera preview'}">
              ${this.isLocalMinimized ? '▲ Expand' : '▼ Minimize'}
            </button>
          </div>
        </div>
        <div class="cam-video-wrap" style="display: ${this.isLocalMinimized ? 'none' : 'block'};">
          <video id="localCamVideo" class="cam-video cam-video-mirrored" autoplay playsinline muted></video>
          <div class="cam-paused-overlay" style="display: ${this.isPaused ? 'flex' : 'none'};"><span>⏸️ Camera Paused</span></div>
        </div>
        <div class="cam-actions">
          <button type="button" class="btn-cam-action ${this.isPaused ? 'btn-cam-resume' : 'btn-cam-pause'}" id="btnLocalCamPause">
            ${this.isPaused ? '▶ Resume' : '⏸ Pause'}
          </button>
          <button type="button" class="btn-cam-action btn-cam-off" id="btnLocalCamOff">
            ✕ Turn Off
          </button>
        </div>
      `;
      targetParent.prepend(localWrap);

      const btnMinimize = localWrap.querySelector('.btn-cam-minimize-local');
      if (btnMinimize) {
        btnMinimize.onclick = (e) => {
          e.preventDefault();
          this.toggleMinimizeLocalCam(context);
        };
      }

      const btnPause = localWrap.querySelector('#btnLocalCamPause');
      const btnOff = localWrap.querySelector('#btnLocalCamOff');
      if (btnPause) {
        btnPause.onclick = (e) => {
          e.preventDefault();
          if (this.isPaused) {
            this.resumeBroadcast(context);
          } else {
            this.pauseBroadcast(context);
          }
        };
      }
      if (btnOff) {
        btnOff.onclick = (e) => {
          e.preventDefault();
          this.stopBroadcast(context);
        };
      }
    } else {
      localWrap.classList.toggle('is-minimized', this.isLocalMinimized);

      const nameEl = localWrap.querySelector('.cam-name');
      if (nameEl) nameEl.textContent = `📹 ${myName} (You)`;

      const badgeEl = localWrap.querySelector('.cam-badge');
      if (badgeEl) {
        badgeEl.className = `cam-badge ${this.isPaused ? 'cam-badge-paused' : 'cam-badge-live'}`;
        badgeEl.textContent = this.isPaused ? '⏸️ Paused' : '● Live';
      }

      const toggleBtn = localWrap.querySelector('.btn-cam-minimize-local');
      if (toggleBtn) {
        toggleBtn.textContent = this.isLocalMinimized ? '▲ Expand' : '▼ Minimize';
        toggleBtn.title = this.isLocalMinimized ? 'Expand camera preview' : 'Minimize camera preview';
      }

      const overlay = localWrap.querySelector('.cam-paused-overlay');
      if (overlay) overlay.style.display = this.isPaused ? 'flex' : 'none';

      const videoWrap = localWrap.querySelector('.cam-video-wrap');
      if (videoWrap) {
        videoWrap.style.display = this.isLocalMinimized ? 'none' : 'block';
      }

      const btnPause = localWrap.querySelector('#btnLocalCamPause');
      if (btnPause) {
        btnPause.className = `btn-cam-action ${this.isPaused ? 'btn-cam-resume' : 'btn-cam-pause'}`;
        btnPause.textContent = this.isPaused ? '▶ Resume' : '⏸ Pause';
      }

      const resizeHandle = localWrap.querySelector('.cam-resize-handle');
      if (resizeHandle) {
        resizeHandle.style.display = this.isLocalMinimized ? 'none' : 'flex';
      }
    }

    const videoEl = localWrap.querySelector('#localCamVideo');
    if (videoEl && this.localStream) {
      this._attachStreamToVideo(videoEl, this.localStream, doc);
    }
    this.makeTileResizable(localWrap, 180, context);
    if (this.isLocalMinimized) {
      const resizeHandle = localWrap.querySelector('.cam-resize-handle');
      if (resizeHandle) resizeHandle.style.display = 'none';
    }
  }

  /**
   * Renders or updates a remote opponent's video tile on the game table.
   * @param {string} seatId
   * @param {MediaStream} stream
   * @param {string} name
   * @param {boolean} paused
   * @param {Object} [context]
   */
  renderRemoteTile(seatId, stream, name, paused = false, context = {}) {
    const doc = context.document || (typeof document !== 'undefined' ? document : null);
    if (!doc) return;

    let container = doc.getElementById('remoteCamsContainer');
    if (!container) {
      const oppWrapper = doc.getElementById('opponentsWrapper');
      const tableRight = doc.getElementById('tableRight');
      const targetParent = oppWrapper || tableRight;
      if (!targetParent) return;

      container = doc.createElement('div');
      container.id = 'remoteCamsContainer';
      container.className = 'remote-cams-container';
      targetParent.prepend(container);
    }

    let tile = doc.getElementById(`remoteCamTile_${seatId}`);
    const isMinimized = this.hiddenSeats.has(seatId);

    if (!tile) {
      tile = doc.createElement('div');
      tile.id = `remoteCamTile_${seatId}`;
      tile.dataset.seat = seatId;
      tile.className = `cam-tile cam-tile-remote ${isMinimized ? 'is-minimized is-hidden' : ''}`;

      tile.innerHTML = `
        <div class="cam-header">
          <span class="cam-name">📹 ${name || 'Opponent'}</span>
          <div class="cam-header-right">
            <span class="cam-badge ${paused ? 'cam-badge-paused' : 'cam-badge-live'}">
              ${paused ? '⏸️ Paused' : '● Live'}
            </span>
            <button type="button" class="btn-cam-hide-toggle btn-cam-minimize btn-cam-minimize-remote" title="${isMinimized ? 'Expand camera' : 'Minimize camera'}">
              ${isMinimized ? '▲ Expand' : '▼ Minimize'}
            </button>
          </div>
        </div>
        <div class="cam-video-wrap" style="display: ${isMinimized ? 'none' : 'block'};">
          <video id="remoteVideo_${seatId}" class="cam-video" autoplay playsinline muted></video>
          <div class="cam-paused-overlay" style="display: ${paused ? 'flex' : 'none'};"><span>⏸️ Camera Paused</span></div>
        </div>
      `;
      container.appendChild(tile);

      const btnMinimize = tile.querySelector('.btn-cam-hide-toggle');
      if (btnMinimize) {
        btnMinimize.onclick = (e) => {
          e.preventDefault();
          this.toggleHideRemoteCam(seatId, context);
        };
      }
    } else {
      tile.className = `cam-tile cam-tile-remote ${isMinimized ? 'is-minimized is-hidden' : ''}`;
      const nameEl = tile.querySelector('.cam-name');
      if (nameEl && name) nameEl.textContent = `📹 ${name}`;

      const badgeEl = tile.querySelector('.cam-badge');
      if (badgeEl) {
        badgeEl.className = `cam-badge ${paused ? 'cam-badge-paused' : 'cam-badge-live'}`;
        badgeEl.textContent = paused ? '⏸️ Paused' : '● Live';
      }

      const wrapEl = tile.querySelector('.cam-video-wrap');
      if (wrapEl) wrapEl.style.display = isMinimized ? 'none' : 'block';

      let pausedOverlay = tile.querySelector('.cam-paused-overlay');
      if (pausedOverlay) {
        pausedOverlay.style.display = paused ? 'flex' : 'none';
      } else if (paused && wrapEl) {
        pausedOverlay = doc.createElement('div');
        pausedOverlay.className = 'cam-paused-overlay';
        pausedOverlay.innerHTML = '<span>⏸️ Camera Paused</span>';
        wrapEl.appendChild(pausedOverlay);
      }

      const btnMinimize = tile.querySelector('.btn-cam-hide-toggle');
      if (btnMinimize) {
        btnMinimize.textContent = isMinimized ? '▲ Expand' : '▼ Minimize';
        btnMinimize.title = isMinimized ? 'Expand camera' : 'Minimize camera';
      }

      const resizeHandle = tile.querySelector('.cam-resize-handle');
      if (resizeHandle) {
        resizeHandle.style.display = isMinimized ? 'none' : 'flex';
      }
    }

    const videoEl = tile.querySelector(`#remoteVideo_${seatId}`);
    if (videoEl && stream) {
      this._attachStreamToVideo(videoEl, stream, doc);
    }
    this.makeTileResizable(tile, 210, context);
    if (isMinimized) {
      const resizeHandle = tile.querySelector('.cam-resize-handle');
      if (resizeHandle) resizeHandle.style.display = 'none';
    }
  }

  /**
   * Enables mouse & touch drag-to-resize on a camera tile and persists size preferences.
   * @param {HTMLElement} tile
   * @param {number} defaultWidthPx
   * @param {Object} [context]
   */
  makeTileResizable(tile, defaultWidthPx = 200, context = {}) {
    if (!tile || tile._hasResizeHandler) return;
    tile._hasResizeHandler = true;

    const doc = context.document || (typeof document !== 'undefined' ? document : null);
    const storageKey = `cc_cam_width_${tile.id || 'tile'}`;

    // Restore saved size if available
    try {
      const s = getStorage();
      if (s) {
        const saved = s.getItem(storageKey);
        if (saved) {
          const w = parseInt(saved, 10);
          if (w >= 130 && w <= 600) {
            tile.style.width = `${w}px`;
          }
        }
      }
    } catch (e) {}

    // Add or locate resize handle
    let handle = tile.querySelector ? tile.querySelector('.cam-resize-handle') : null;
    if (!handle && doc && typeof doc.createElement === 'function') {
      handle = doc.createElement('div');
      handle.className = 'cam-resize-handle';
      handle.title = 'Drag to resize camera box (Double-click to reset size)';
      if (typeof handle.setAttribute === 'function') {
        handle.setAttribute('aria-label', 'Resize camera box');
      }
      handle.innerHTML = `<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path d="M10 2L2 10M10 6L6 10M10 10L10 10.01" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
      </svg>`;
      tile.appendChild(handle);
    }

    if (!handle || typeof handle.addEventListener !== 'function') return;

    let isResizing = false;
    let startX = 0;
    let startWidth = 0;

    const onPointerDown = (e) => {
      if (e.type === 'mousedown' && e.button !== 0) return;
      isResizing = true;
      if (tile.classList && typeof tile.classList.add === 'function') {
        tile.classList.add('is-resizing');
      }

      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      startX = clientX;
      startWidth = tile.getBoundingClientRect ? tile.getBoundingClientRect().width : (parseInt(tile.style.width, 10) || defaultWidthPx);

      const onPointerMove = (ev) => {
        if (!isResizing) return;
        const currentX = ev.touches ? ev.touches[0].clientX : ev.clientX;
        const deltaX = currentX - startX;
        const minW = 140;
        const maxW = typeof window !== 'undefined' ? Math.min(540, window.innerWidth - 30) : 540;
        const newW = Math.max(minW, Math.min(maxW, Math.round(startWidth + deltaX)));

        tile.style.width = `${newW}px`;
        if (typeof ev.preventDefault === 'function') ev.preventDefault();
      };

      const onPointerUp = () => {
        if (!isResizing) return;
        isResizing = false;
        if (tile.classList && typeof tile.classList.remove === 'function') {
          tile.classList.remove('is-resizing');
        }

        if (typeof window !== 'undefined') {
          window.removeEventListener('mousemove', onPointerMove);
          window.removeEventListener('touchmove', onPointerMove);
          window.removeEventListener('mouseup', onPointerUp);
          window.removeEventListener('touchend', onPointerUp);
          window.removeEventListener('touchcancel', onPointerUp);
        }

        try {
          const s = getStorage();
          if (s) {
            const currentW = tile.getBoundingClientRect ? Math.round(tile.getBoundingClientRect().width) : parseInt(tile.style.width, 10);
            if (currentW) s.setItem(storageKey, currentW.toString());
          }
        } catch (err) {}
      };

      if (typeof window !== 'undefined') {
        window.addEventListener('mousemove', onPointerMove, { passive: false });
        window.addEventListener('touchmove', onPointerMove, { passive: false });
        window.addEventListener('mouseup', onPointerUp);
        window.addEventListener('touchend', onPointerUp);
        window.addEventListener('touchcancel', onPointerUp);
      }

      if (typeof e.preventDefault === 'function') e.preventDefault();
      if (typeof e.stopPropagation === 'function') e.stopPropagation();
    };

    handle.addEventListener('mousedown', onPointerDown);
    handle.addEventListener('touchstart', onPointerDown, { passive: false });

    handle.addEventListener('dblclick', (e) => {
      if (typeof e.preventDefault === 'function') e.preventDefault();
      if (typeof e.stopPropagation === 'function') e.stopPropagation();
      tile.style.width = `${defaultWidthPx}px`;
      try {
        const s = getStorage();
        if (s) {
          s.removeItem(storageKey);
        }
      } catch (err) {}
    });
  }

  /**
   * Removes a remote opponent's video tile from the game table.
   * @param {string} seatId
   * @param {Object} [context]
   */
  removeRemoteTile(seatId, context = {}) {
    const doc = context.document || (typeof document !== 'undefined' ? document : null);
    if (!doc) return;
    const tile = doc.getElementById(`remoteCamTile_${seatId}`);
    if (tile) tile.remove();
  }

  /**
   * Updates the match header camera toggle button state (Off / Live / Paused).
   * @param {Object} [context]
   */
  updateCamButtonUi(context = {}) {
    const doc = context.document || (typeof document !== 'undefined' ? document : null);
    if (!doc) return;
    const btn = doc.getElementById('btnCamToggle');
    if (!btn) return;

    if (!this.isBroadcasting) {
      btn.textContent = '📷 Broadcast Cam';
      btn.className = 'btn-ghost';
      btn.title = 'Start broadcasting your camera to online players';
    } else if (this.isPaused) {
      btn.textContent = '⏸️ Cam Paused';
      btn.className = 'btn-ghost cam-btn-active is-paused';
      btn.title = 'Camera is paused — click to resume';
    } else {
      btn.textContent = '🔴 Cam Live';
      btn.className = 'btn-ghost cam-btn-active is-live';
      btn.title = 'Camera is broadcasting live — click to pause/stop';
    }
  }

  resolveSeatIdForPeer(peerId, context = {}) {
    const net = context.net || (typeof window !== 'undefined' ? window.net : null);
    if (!net || !peerId) return null;
    if (net.isHost && net.clients) {
      const client = net.clients.get(peerId);
      return client ? client.seatId : null;
    }
    if (!net.isHost && net.room && peerId.includes(net.room.trim().toUpperCase())) {
      return 'p1';
    }
    return null;
  }

  resolveNameForSeat(seatId, context = {}) {
    const pName = context.pName || (typeof window !== 'undefined' ? window.pName : null);
    if (typeof pName === 'function') {
      return pName(seatId);
    }
    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : null);
    if (settings && Array.isArray(settings.seats)) {
      const s = settings.seats.find(x => x && x.id === seatId);
      if (s && s.name) return s.name;
    }
    return seatId ? `Player ${seatId.replace(/\D/g, '') || ''}` : 'Player';
  }
}
